<template>
  <div class="sip-card">
    <!-- Header Section -->
    <div
      class="d-flex align-center justify-space-between"
      style="
        position: sticky;
        top: 0;
        z-index: 2;
        background-color: white;
        padding-top: 12px;
        border-bottom: 1px solid #e4e4e4;
      "
    >
      <div class="sip-card-header">
        <span class="sip-card-title">SIPペイロード</span>
        <span class="sip-card-subtitle">すべてのSIPペイロードの詳細</span>
      </div>
      <div>
        <v-btn icon @click="closeSIPDataModal">
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </div>
    </div>
    <!-- No SIP Data Found -->
    <div v-if="SIPData.length === 0">
      <div class="mt-5" flat>
        <v-card-text class="text-center">
          <v-icon class="primary--text" size="100"
            >mdi-alert-circle-outline</v-icon
          >
          <h3 class="mt-5 primary--text">SIPデータが見つかりません。</h3>
        </v-card-text>
      </div>
    </div>
    <!-- SIP Call Flow -->
    <div v-else>
      <div class="call-flow">
        <h2>SIP Call Flow</h2>
        <ul>
          <li v-for="(event, index) in callFlow" :key="index">
            <strong>{{ event.event }}</strong>
            <span v-if="event.from"> From: {{ event.from }}</span>
            <span v-if="event.to"> → {{ event.to }}</span>
            <p v-if="event.details">{{ event.details }}</p>
            <p v-if="event?.arrivalTime">到達時刻 : {{ event.arrivalTime }}</p>
          </li>
        </ul>
      </div>

      <!-- SIP Payload Data -->
      <div v-for="sip in SIPData" :key="sip.id">
        <div class="py-2">
          <VueMarkdown>
            {{
              sip?.Full_SIP_Payload?.replace(
                /(^|\s)(\b(To|From|Call-ID|CSeq|x-inin-cnv|Event)):/g,
                "$1**$2**:"
              )
            }}
          </VueMarkdown>
          <VueMarkdown v-if="sip?.arrival_time">
            到達時刻 : 
            {{
              sip?.arrival_time 
              ? 
                new Date(sip.arrival_time).toLocaleString("en-CA", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false
                })
              : "N/A"
            }}
            </VueMarkdown>
        </div>
        <v-divider></v-divider>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from "vue";
import VueMarkdown from "vue-markdown";

export default defineComponent({
  name: "SIPDataModalView",
  components: { VueMarkdown },
  props: {
    SIPData: {
      type: Array,
      required: true,
    },
    closeSIPDataModal: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const parseCallFlow = () => {
      const callFlow = [];
      let firstNotifyEncountered = false;
      let pendingRequest = null; // holds 'hold' or 'talk' for re-INVITE/ACK pairing

      props.SIPData.forEach((packet) => {
        const fullPayload = packet.Full_SIP_Payload;
        const statusRegex = /^SIP\/2\.0\s+(\d{3})\s+/i;
        const statusMatch = fullPayload.match(statusRegex);
        const eventRegex = /Event:\s*(talk|hold|none)/i;
        const eventMatch = fullPayload.match(eventRegex);
        const eventStatus = eventMatch ? eventMatch[1].toLowerCase() : null;
        const { From, To, CSEQ } = packet;
        const statusCode = statusMatch ? statusMatch[1] : "";
        const arrivalTime = packet.arrival_time 
          ? new Date(packet.arrival_time).toLocaleString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
          })
          : "N/A";

        let eventText = "";

        if (CSEQ.includes("NOTIFY")) {
          if (!firstNotifyEncountered) {
            // first NOTIFY always means ringing regardless of event value
            eventText = "🔔 Ringing (NOTIFY)";
            firstNotifyEncountered = true;
          } else {
            // subsequent NOTIFY uses its event value
            if (eventStatus === "hold") {
              eventText = "🔔 Hold Request (NOTIFY)";
              pendingRequest = "hold";
            } else if (eventStatus === "talk") {
              eventText = "🔔 Talk Request (NOTIFY)";
              pendingRequest = "talk";
            } else {
              eventText = "🔔 NOTIFY";
            }
          }
        } else if (statusCode === "200") {
          eventText = "✅ Call Received (200 OK)";
        } else if (CSEQ.includes("INVITE")) {
          // if the INVITE is coming from the party that is not the original caller,
          // assume it is in response to a hold/talk request.
          if (pendingRequest) {
            eventText =
              pendingRequest === "hold"
                ? "🔜 Hold Request Initiated (INVITE)"
                : "🔜 Talk Request Initiated (INVITE)";
            // leave pendingRequest to be used by the subsequent ACK
          } else {
            eventText = "🔜 Call Initiated (INVITE)";
          }
        } else if (CSEQ.includes("ACK")) {
          if (pendingRequest) {
            eventText =
              pendingRequest === "hold"
                ? "🟢 Hold Request Acknowledged (ACK)"
                : "🟢 Talk Request Acknowledged (ACK)";
            pendingRequest = null; // clear after use
          } else {
            eventText = "🟢 Call Acknowledged (ACK)";
          }
        } else if (CSEQ.includes("BYE")) {
          eventText = "🚫 Call Terminated (BYE)";
        }

        callFlow.push({
          event: eventText,
          from: From,
          to: To,
          details: `CSeq: ${CSEQ}${
            statusCode === "200" ? ", RTP Established" : ""
          }`,
          arrivalTime: arrivalTime,
        });
      });

      return callFlow;
    };

    const callFlow = computed(() => parseCallFlow());

    return { callFlow };
  },
});
</script>

<style scoped>
.sip-card {
  height: calc(100vh - 100px);
  border-radius: 12px;
  border-width: 0.5px;
  padding: 0px 20px 20px 20px;
  border: 0.5px solid #e2e2e2;
  background-color: white;
  overflow-y: auto;
  scrollbar-width: thin;
}

.sip-card-header {
  display: flex;
  flex-direction: column;
  align-items: start;
  margin-bottom: 10px;
}

.sip-card-title {
  color: #194fa2;
  font-weight: 500;
  font-size: 16px;
}

.sip-card-subtitle {
  color: #3f4652;
  font-weight: 400;
  font-size: 13px;
}

.call-flow {
  font-family: Arial, sans-serif;
  background: #fdfdfd;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e4e4e4;
  margin: 10px 0px;
}

ul {
  list-style: none;
  padding-left: 10px;
}

li {
  padding: 8px;
  border-left: 3px solid #007bff;
  margin-bottom: 5px;
}

strong {
  color: #007bff;
}

p {
  font-size: 0.9em;
  color: #555;
  margin-left: 15px;
}
</style>
